"use client";

import {
	createInlineFlow,
	type InlineFlow,
} from "@getuserfeedback/sdk/internal/inline-flow";
import { useCallback, useContext, useRef, useState } from "react";
import {
	GetUserFeedbackContext,
	type UseCustomFlowReturn,
	type UseFlowControllerOptions,
	type UseFlowReturn,
	useFlowRun,
} from "../client";

/**
 * Internal supplied-flow options; intentionally absent from the public root.
 * The hook captures one provider client and immutable definition snapshot for
 * its mounted lifetime; remount the consumer to change either one.
 *
 * @see docs/specs/2026-08-02-flow-interaction-model.md
 */
export interface UseFlowOptions {
	flow: InlineFlow;
	prefetchOnMount?: boolean;
	/** Remount the hook to change container mode. */
	container?: "default" | "custom";
	hideCloseButton?: boolean;
}

export function useFlow(
	options: UseFlowOptions & { container?: "default" },
): UseFlowReturn;
export function useFlow(
	options: UseFlowOptions & { container: "custom" },
): UseCustomFlowReturn;
export function useFlow(
	options: UseFlowOptions,
): UseFlowReturn | UseCustomFlowReturn {
	const context = useContext(GetUserFeedbackContext);
	if (!context) {
		throw new Error("useFlow must be used within a GetUserFeedbackProvider");
	}
	const initialClientRef = useRef(context.client);
	if (initialClientRef.current !== context.client) {
		throw new Error(
			"useFlow must be remounted when the GetUserFeedbackProvider client changes",
		);
	}
	const [flowRun] = useState(() =>
		createInlineFlow(context.client, options.flow),
	);
	const openFlow = useCallback<UseFlowControllerOptions["openFlow"]>(
		({ container, hideCloseButton, metadata }) => {
			if (container !== undefined) {
				flowRun.setContainer(container);
			}
			return flowRun.open({ hideCloseButton, metadata });
		},
		[flowRun],
	);
	const prerenderFlow = useCallback<UseFlowControllerOptions["prerenderFlow"]>(
		({ container, hideCloseButton }) => {
			if (container !== undefined) {
				flowRun.setContainer(container);
			}
			return flowRun.prerender({ hideCloseButton });
		},
		[flowRun],
	);
	return useFlowRun({
		flowRun,
		openFlow,
		prerenderFlow,
		container: options.container,
		prefetchOnMount: options.prefetchOnMount,
		hideCloseButton: options.hideCloseButton,
	});
}

export type { InlineFlow } from "@getuserfeedback/sdk/internal/inline-flow";
export type { UseCustomFlowReturn, UseFlowReturn } from "../client";
